import DialogModal from 'src/components/dialog_modal/dialog_modal.vue'
import ReactionEmoji from 'src/components/reaction_emoji/reaction_emoji.vue'
import UserListRow from 'src/components/user_list_row/user_list_row.vue'

import { useUsersStore } from 'src/stores/users.js'

// Who favorited, repeated, quoted or reacted, one list at a time.
// A category: { name, label, count, icon or emoji: { name, url },
// ids (a Set, or undefined while unknown), load, hasMore, loadMore }
const ParticipantsDialog = {
  name: 'ParticipantsDialog',
  props: {
    categories: { type: Array, required: true },
    initial: String,
  },
  emits: ['close'],
  components: { DialogModal, ReactionEmoji, UserListRow },
  data() {
    return {
      selected: this.initial,
      loading: false,
      failed: false,
      // the last load or load more, so an older answer changes nothing
      request: 0,
      lastLoad: null,
    }
  },
  computed: {
    category() {
      return (
        this.categories.find((c) => c.name === this.selected) ||
        this.categories[0]
      )
    },
    users() {
      return [...(this.category?.ids ?? [])]
        .map((id) => useUsersStore().findUser(id))
        .filter(Boolean)
    },
  },
  watch: {
    'category.name'() {
      this.$refs.list?.scrollTo?.(0, 0)
      this.loadCategory()
    },
  },
  mounted() {
    this.loaded = new Set()
    this.loadCategory()
    // on the document: the keys work also when the focus fell out of it
    document.addEventListener('keydown', this.onKeydown, true)
    // the root is the teleport's placeholder: the dialog is elsewhere
    this.$refs.dialog.$el
      .querySelector('.participants-category.-selected')
      ?.focus()
  },
  unmounted() {
    document.removeEventListener('keydown', this.onKeydown, true)
  },
  methods: {
    // each list once per opening: the counts can be newer than the list,
    // and a second load would drop the further pages loaded (quotes)
    loadCategory() {
      const category = this.category
      if (!category || this.loaded.has(category.name)) return
      this.loaded.add(category.name)
      this.run(() => category.load()).then((ok) => {
        if (!ok) this.loaded.delete(category.name)
      })
    },
    // a load or a load more; an older answer changes nothing
    async run(load) {
      const request = ++this.request
      this.lastLoad = load
      this.loading = true
      this.failed = false
      try {
        await load()
        return true
      } catch {
        if (request === this.request) this.failed = true
        return false
      } finally {
        if (request === this.request) this.loading = false
      }
    },
    retry() {
      this.run(this.lastLoad)
    },
    more() {
      if (!this.loading) this.run(() => this.category.loadMore())
    },
    close() {
      this.$emit('close')
    },
    // Escape closes; Tab stays in the dialog
    onKeydown(event) {
      if (event.key === 'Escape') return this.close()
      if (event.key !== 'Tab') return
      const dialog = this.$refs.dialog.$el
      const items = [
        ...dialog.querySelectorAll('button:not(:disabled), a[href]'),
      ]
      const first = items[0]
      const last = items.at(-1)
      const target = !dialog.contains(document.activeElement)
        ? first
        : event.shiftKey && document.activeElement === first
          ? last
          : !event.shiftKey && document.activeElement === last
            ? first
            : null
      if (target) {
        event.preventDefault()
        target.focus()
      }
    },
  },
}

export default ParticipantsDialog
